import {
  BiRegularGlobe,
  BiRegularImageAlt,
  BiRegularLinkExternal,
} from "solid-icons/bi";
import { BsGithub, BsLinkedin, BsTwitterX } from "solid-icons/bs";
import { type Component, createMemo, For, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { useI18n } from "@/api/i18n";
import { ensureProtocol, formatUrlHost } from "@/libs/url";

type PortfolioLink = {
  key: string;
  label: string;
  url: string;
  icon: typeof BiRegularGlobe;
};

/**
 * Prominent cards for the portfolio / website / social links a freelancer shares.
 * Presentational and self-hiding: pass the raw values, blanks are dropped and
 * the whole section renders nothing when no link is present. Socials (LinkedIn,
 * GitHub, X) live here rather than in a separate "contacts" block.
 */
export const PortfolioLinks: Component<{
  portfolio?: string | null;
  website?: string | null;
  linkedin?: string | null;
  github?: string | null;
  twitter?: string | null;
}> = (props) => {
  const { t } = useI18n();
  const links = createMemo<PortfolioLink[]>(() => {
    const out: PortfolioLink[] = [];
    const portfolio = props.portfolio?.trim();
    const website = props.website?.trim();
    const linkedin = props.linkedin?.trim();
    const github = props.github?.trim();
    const twitter = props.twitter?.trim();
    if (portfolio)
      out.push({
        key: "portfolio",
        label: t("profile.details.portfolio.portfolio") as string,
        url: portfolio,
        icon: BiRegularImageAlt,
      });
    if (website)
      out.push({
        key: "website",
        label: t("profile.details.portfolio.website") as string,
        url: website,
        icon: BiRegularGlobe,
      });
    if (linkedin)
      out.push({
        key: "linkedin",
        label: "LinkedIn",
        url: linkedin,
        icon: BsLinkedin,
      });
    if (github)
      out.push({
        key: "github",
        label: "GitHub",
        url: github,
        icon: BsGithub,
      });
    if (twitter)
      out.push({
        key: "twitter",
        label: "X",
        url: twitter,
        icon: BsTwitterX,
      });
    return out;
  });

  return (
    <Show when={links().length > 0}>
      <section class="rounded-xl border border-matisse-200 bg-matisse-50/60 p-4">
        <h3 class="mb-3 text-xs font-semibold uppercase tracking-wide text-matisse-700">
          {t("profile.details.portfolio.title")}
        </h3>
        <div class="space-y-2">
          <For each={links()}>
            {(link) => (
              <a
                href={ensureProtocol(link.url)}
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-3 rounded-lg border border-matisse-100 bg-white p-3 transition-colors hover:border-matisse-300"
              >
                <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-matisse-50 text-matisse-600">
                  <Dynamic component={link.icon} class="h-5 w-5" />
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-gray-900">{link.label}</p>
                  <p class="truncate text-sm text-matisse-600">
                    {formatUrlHost(link.url)}
                  </p>
                </div>
                <BiRegularLinkExternal class="h-4 w-4 shrink-0 text-gray-400" />
              </a>
            )}
          </For>
        </div>
      </section>
    </Show>
  );
};
